import type { AxiosInstance } from "axios";
import { isAxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import type {
  CreateImageRequest,
  CreateImageResult,
  CreateVideoRequest,
  CreateVideoResult,
  ProjectTransactionNotification,
  TransactionCompletedResponse,
  TransactionStatus
} from "@/types/api";
import { PROJECT_TRANSACTION_NOTIFICATIONS_INTERVAL_MS } from "@/utils/constants";
import { getErrorMessage, throwLockAwareError } from "@/api/apiClient";

/** Lambda prefixes failed-job text with `transaction has errored `; strip for UI. */
const TRANSACTION_ERRORED_PREFIX = /^transaction has errored\s+/i;

/**
 * Turn raw transaction error strings into clearer copy (e.g. moderation rejections from image providers).
 */
export function humanizeTransactionErrorMessage(raw: string): string {
  const body = raw.replace(TRANSACTION_ERRORED_PREFIX, "").trim();
  if (body.length === 0) {
    return raw.trim();
  }

  /** Runway/Gemini and similar APIs use varied wording for policy blocks — treat alike for UI. */
  if (/moderation|content policy|safety|blocked|not allowed|policy violation/i.test(body)) {
    return "The image provider rejected your prompt (safety or content policy). Try rephrasing or using a shorter, more neutral description.";
  }

  return body;
}

/** Matches Lambda `create_response(202, "transaction is processing")` body (plain text, case-insensitive). */
const LAMBDA_PENDING_BODY = "transaction is processing";

/**
 * Unwrap JSON strings (sometimes double-encoded in API responses) until we get an object or plain text.
 */
function unwrapTransactionPayload(payload: unknown, depth = 0): unknown {
  if (depth > 6) {
    return payload;
  }

  if (typeof payload === "string") {
    const t = payload.trim();
    if (t === "") {
      return {};
    }

    try {
      const parsed = JSON.parse(t) as unknown;
      return unwrapTransactionPayload(parsed, depth + 1);
    } catch {
      return payload;
    }
  }

  return payload;
}

function isLambdaPendingPlainText(value: unknown): boolean {
  return typeof value === "string" && value.trim().toLowerCase() === LAMBDA_PENDING_BODY;
}

function isPendingStatusObject(value: unknown): boolean {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const s = (value as { status?: unknown }).status;
  if (typeof s !== "string") {
    return false;
  }

  const lower = s.trim().toLowerCase();
  return lower === "pending" || lower === "processing" || lower === "queued" || lower === "running";
}

/**
 * Lambda `do_get_transaction` returns plain text for some statuses (e.g. 500 + `"transaction has errored …"`,
 * 202 + `"transaction is processing"`), not JSON. Default axios JSON parsing throws; we parse safely and fall back to string.
 *
 * Some gateways mis-map status codes (e.g. HTTP 200 while the job is still queued). We treat known pending bodies
 * as `pending` so polling stops only when the payload is a real completion object (e.g. `{ id }`).
 */
export const fetchTransactionStatus = async (axiosClient: AxiosInstance, transactionId: string): Promise<TransactionStatus> => {
  try {
    const response = await axiosClient.get<unknown>(`/transaction/${encodeURIComponent(transactionId)}`, {
      validateStatus: () => true,
      transformResponse: [
        (raw: string): unknown => {
          if (raw === "" || raw === undefined) {
            return {};
          }

          try {
            return JSON.parse(raw) as unknown;
          } catch {
            return raw;
          }
        }
      ]
    });

    const payload = response.data;
    const unwrapped = unwrapTransactionPayload(payload);

    if (response.status === 202) {
      const data: Record<string, unknown> =
        unwrapped !== null && typeof unwrapped === "object" && !Array.isArray(unwrapped)
          ? (unwrapped as Record<string, unknown>)
          : { message: typeof unwrapped === "string" ? unwrapped : "" };
      return { status: "pending", httpStatus: 202, data };
    }

    if (response.status === 502 || response.status === 503 || response.status === 504) {
      return { status: "pending", httpStatus: response.status, data: { message: `gateway ${response.status}` } };
    }

    if (response.status === 200) {
      if (isLambdaPendingPlainText(unwrapped)) {
        return { status: "pending", httpStatus: 202, data: { message: LAMBDA_PENDING_BODY } };
      }

      if (isPendingStatusObject(unwrapped)) {
        return { status: "pending", httpStatus: 202, data: unwrapped as Record<string, unknown> };
      }

      const data =
        unwrapped !== null && typeof unwrapped === "object" && !Array.isArray(unwrapped)
          ? (unwrapped as Record<string, unknown> | TransactionCompletedResponse)
          : ({} as Record<string, unknown>);
      return { status: "completed", httpStatus: 200, data };
    }

    let message = `Transaction failed (${response.status}).`;
    if (typeof payload === "string") {
      message = humanizeTransactionErrorMessage(payload);
    } else if (payload !== null && typeof payload === "object") {
      const o = payload as { message?: unknown; error?: unknown };
      if (typeof o.message === "string" && o.message.length > 0) {
        message = humanizeTransactionErrorMessage(o.message);
      } else if (typeof o.error === "string" && o.error.length > 0) {
        message = humanizeTransactionErrorMessage(o.error);
      }
    }

    return { status: "error", httpStatus: response.status, message };
  } catch (error) {
    // API Gateway 504 / network blips often surface as CORS errors in the browser; keep polling.
    if (isAxiosError(error) && error.response === undefined) {
      return { status: "pending", httpStatus: 0, data: { message: "network error" } };
    }

    throw new Error(getErrorMessage(error, "Something went wrong when getting transaction status."));
  }
};

const TRANSACTION_STATUS_QUEUED = 0;
const TRANSACTION_STATUS_COMPLETED = 1;
const TRANSACTION_STATUS_ERRORED = -1;
const TRANSACTION_STATUS_TIMED_OUT = -2;
const TRANSACTION_STATUS_SUPERSEDED = -3;

function parseProjectTransactionNotificationRow(row: unknown): ProjectTransactionNotification | null {
  if (row === null || typeof row !== "object" || Array.isArray(row)) {
    return null;
  }

  const record = row as Record<string, unknown>;
  const transactionId = typeof record["transaction_id"] === "string" ? record["transaction_id"] : null;
  const projectId = typeof record["project_id"] === "string" ? record["project_id"] : null;
  const userId = typeof record["user_id"] === "string" ? record["user_id"] : null;
  const transactionType = typeof record["transaction_type"] === "string" ? record["transaction_type"] : null;
  const transactionStatus = typeof record["transaction_status"] === "number" ? record["transaction_status"] : null;

  if (!transactionId || !projectId || !userId || !transactionType || transactionStatus === null) {
    return null;
  }

  const displayName = typeof record["display_name"] === "string" ? record["display_name"] : null;
  const timestampStart = typeof record["timestamp_start"] === "number" ? record["timestamp_start"] : undefined;
  const output = typeof record["output"] === "string" ? record["output"] : null;
  const notification = record["notification"] === true;
  const tagType = typeof record["tag_type"] === "string" ? record["tag_type"] : null;
  const tagId = typeof record["tag_id"] === "string" ? record["tag_id"] : null;
  const entityInfoJson = typeof record["entity_info_json"] === "string" ? record["entity_info_json"] : null;
  const modelId = typeof record["model_id"] === "string" ? record["model_id"] : null;
  const request = typeof record["request"] === "string" ? record["request"] : null;

  return {
    transaction_id: transactionId,
    project_id: projectId,
    user_id: userId,
    display_name: displayName,
    transaction_type: transactionType,
    transaction_status: transactionStatus,
    timestamp_start: timestampStart,
    output,
    notification,
    tag_type: tagType,
    tag_id: tagId,
    entity_info_json: entityInfoJson,
    model_id: modelId,
    request
  };
}

/** GET /transaction/project/{project-id} — active image/video generation notifications. */
export const fetchProjectTransactionNotifications = async (
  axiosClient: AxiosInstance,
  projectId: string
): Promise<readonly ProjectTransactionNotification[]> => {
  try {
    const response = await axiosClient.get<unknown>(`/transaction/project/${encodeURIComponent(projectId)}`);
    if (!Array.isArray(response.data)) {
      return [];
    }

    return response.data
      .map((row) => parseProjectTransactionNotificationRow(row))
      .filter((row): row is ProjectTransactionNotification => row !== null)
      .toSorted((a, b) => (b.timestamp_start ?? 0) - (a.timestamp_start ?? 0));
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when loading project notifications."));
  }
};

/** PATCH /transaction/{id} — clear the notification flag. */
export const clearTransactionNotification = async (axiosClient: AxiosInstance, transactionId: string): Promise<void> => {
  try {
    await axiosClient.patch(`/transaction/${encodeURIComponent(transactionId)}`);
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when clearing the notification."));
  }
};

export const isProjectTransactionInProgress = (transactionStatus: number): boolean => transactionStatus === TRANSACTION_STATUS_QUEUED;

export const isProjectTransactionErrored = (transactionStatus: number): boolean =>
  transactionStatus === TRANSACTION_STATUS_ERRORED || transactionStatus === TRANSACTION_STATUS_TIMED_OUT;

export const isProjectTransactionCompleted = (transactionStatus: number): boolean => transactionStatus === TRANSACTION_STATUS_COMPLETED;

export const isProjectTransactionSuperseded = (transactionStatus: number): boolean => transactionStatus === TRANSACTION_STATUS_SUPERSEDED;

export const isProjectTransactionTerminal = (transactionStatus: number): boolean =>
  transactionStatus === TRANSACTION_STATUS_COMPLETED ||
  isProjectTransactionErrored(transactionStatus) ||
  isProjectTransactionSuperseded(transactionStatus);

/** GET /transaction/{id} — completed image/video job: `id`, `video_id`, or `image_id` on the JSON body. */
export const parseCompletedTransactionEntityId = (data: unknown): string | undefined => {
  if (typeof data === "string") {
    try {
      return parseCompletedTransactionEntityId(JSON.parse(data) as unknown);
    } catch {
      return undefined;
    }
  }

  if (!data || typeof data !== "object") {
    return undefined;
  }

  const o = data as Record<string, unknown>;
  const raw = o["id"] ?? o["video_id"] ?? o["image_id"];
  return typeof raw === "string" && raw.length > 0 ? raw : undefined;
};

const DEFAULT_TRANSACTION_POLL_MS = 5000;
const DEFAULT_TRANSACTION_POLL_MAX_ATTEMPTS = 200;

/**
 * Polls GET /transaction/{id} until completion (200 + media id) or error.
 * Default interval 5s; throws on timeout or `status: "error"`.
 */
export const pollTransactionUntilCompletedEntityId = async (
  axiosClient: AxiosInstance,
  transactionId: string,
  options?: { readonly pollIntervalMs?: number; readonly maxAttempts?: number }
): Promise<string> => {
  const interval = options?.pollIntervalMs ?? DEFAULT_TRANSACTION_POLL_MS;
  const max = options?.maxAttempts ?? DEFAULT_TRANSACTION_POLL_MAX_ATTEMPTS;

  for (let attempt = 0; attempt < max; attempt += 1) {
    // oxlint-disable-next-line no-await-in-loop -- sequential poll; parallel requests would spam the server
    const status = await fetchTransactionStatus(axiosClient, transactionId);
    if (status.status === "completed") {
      const id = parseCompletedTransactionEntityId(status.data);
      if (!id) {
        throw new Error("Transaction completed but no media id was returned.");
      }

      return id;
    }

    if (status.status === "error") {
      throw new Error(status.message);
    }

    // oxlint-disable-next-line no-await-in-loop -- delay between poll attempts
    await new Promise<void>((resolve) => {
      setTimeout(resolve, interval);
    });
  }

  throw new Error("Timed out waiting for the transaction to complete.");
};

/** Parse transaction / image id from POST `/{projectId}/image` response body. */
const parseImageUploadTransactionIdFromResponse = (data: Record<string, unknown> | string | null | undefined): string | null => {
  if (typeof data === "string") {
    return data;
  }

  if (!data || typeof data !== "object") {
    return null;
  }

  const maybe = data as { readonly transaction_id?: unknown; readonly transactionId?: unknown; readonly id?: unknown };
  const fromTransactionId = typeof maybe.transactionId === "string" ? maybe.transactionId : null;
  if (fromTransactionId) {
    return fromTransactionId;
  }

  const fromTransactionUnderscore = typeof maybe.transaction_id === "string" ? maybe.transaction_id : null;
  if (fromTransactionUnderscore) {
    return fromTransactionUnderscore;
  }

  return typeof maybe.id === "string" ? maybe.id : null;
};

export const createImage = async (axiosClient: AxiosInstance, projectId: string, body: CreateImageRequest): Promise<CreateImageResult> => {
  try {
    const requestBody: Record<string, unknown> = {
      image_model: body.image_model,
      prompt: body.prompt,
      display_name: body.display_name
    };

    if (body.reference_image_ids !== undefined) {
      requestBody["reference_image_ids"] = body.reference_image_ids;
    }

    if (body.aspect_ratio !== undefined && body.aspect_ratio !== "") {
      requestBody["aspect_ratio"] = body.aspect_ratio;
    }

    if (body.resolution !== undefined && body.resolution !== "") {
      requestBody["resolution"] = body.resolution;
    }

    if (body.parent_id !== undefined && body.parent_id !== null) {
      requestBody["parent_id"] = body.parent_id;
    }

    if (body["custom-style-dna"] !== undefined) {
      requestBody["custom-style-dna"] = body["custom-style-dna"];
    }

    if (body.tag_type !== undefined) {
      requestBody["tag_type"] = body.tag_type;
    }

    if (body.tag_id !== undefined) {
      requestBody["tag_id"] = body.tag_id;
    }

    if (body.seed !== undefined) {
      requestBody["seed"] = body.seed;
    }

    if (body.temperature !== undefined) {
      requestBody["temperature"] = body.temperature;
    }

    const response = await axiosClient.post<Record<string, unknown> | string>(`/${encodeURIComponent(projectId)}/image`, requestBody);
    const { data } = response;

    const rawId = parseImageUploadTransactionIdFromResponse(data);

    if (typeof rawId !== "string" || rawId.length === 0) {
      throw new Error("Invalid response: missing transaction id.");
    }

    const dataRecord = typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
    const resolvedSeed = typeof dataRecord["seed"] === "number" ? (dataRecord["seed"] as number) : undefined;
    const resolvedTemperature = typeof dataRecord["temperature"] === "number" ? (dataRecord["temperature"] as number) : undefined;

    return {
      transactionId: rawId,
      ...(resolvedSeed === undefined ? {} : { seed: resolvedSeed }),
      ...(resolvedTemperature === undefined ? {} : { temperature: resolvedTemperature })
    };
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when creating an image.");
  }
};

/**
 * POST /{projectId}/video — async; API returns **202** with `{ transaction_id }` (readme).
 * Optional `video_model` defaults server-side to `runway_ml`; `duration` must match an allowed value for that model from GET /metadata (`video_models[].duration`).
 */
export const createVideo = async (axiosClient: AxiosInstance, projectId: string, body: CreateVideoRequest): Promise<CreateVideoResult> => {
  try {
    const response = await axiosClient.post<{ transaction_id?: string; transactionId?: string; seed?: number }>(
      `/${encodeURIComponent(projectId)}/video`,
      body
    );
    const data = response.data ?? {};
    const rawId = typeof data.transactionId === "string" ? data.transactionId : data.transaction_id;
    if (typeof rawId !== "string" || rawId.length === 0) {
      throw new Error("Invalid response: missing transaction id.");
    }

    return {
      transactionId: rawId,
      ...(typeof data.seed === "number" ? { seed: data.seed } : {})
    };
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when creating a video.");
  }
};

// ===== React Query hooks =====

interface TransactionStatusQueryOptions {
  readonly enabled?: boolean;
  readonly refetchInterval?: number | false | ((query: { readonly state: { readonly data?: TransactionStatus | undefined } }) => number | false);
}

export const useTransactionStatus = (transactionId: string | null, options: TransactionStatusQueryOptions = {}) => {
  const axiosClient = useAxios();
  const { enabled: enabledOverride, refetchInterval } = options;
  const enabled = enabledOverride ?? (typeof transactionId === "string" && transactionId.length > 0);

  return useQuery({
    queryKey: ["transaction", transactionId],
    queryFn: async () => fetchTransactionStatus(axiosClient, transactionId!),
    enabled,
    retry: 1,
    refetchOnWindowFocus: false,
    ...(refetchInterval === undefined ? null : { refetchInterval })
  });
};

export const useProjectTransactionNotifications = (projectId: string | null | undefined, options?: { readonly enabled?: boolean }) => {
  const axiosClient = useAxios();
  const extraEnabled = options?.enabled !== false;

  return useQuery({
    queryKey: ["projectTransactionNotifications", projectId],
    queryFn: async () => fetchProjectTransactionNotifications(axiosClient, projectId!),
    enabled: typeof projectId === "string" && projectId.length > 0 && extraEnabled,
    refetchInterval: PROJECT_TRANSACTION_NOTIFICATIONS_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 1
  });
};

export const useClearTransactionNotification = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => clearTransactionNotification(axiosClient, transactionId),
    onSuccess: (_data, transactionId) => {
      queryClient.setQueriesData<readonly ProjectTransactionNotification[]>(
        { queryKey: ["projectTransactionNotifications"] },
        (existing) => existing?.filter((row) => row.transaction_id !== transactionId) ?? existing
      );
    }
  });
};

export const useCreateImage = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { readonly projectId: string; readonly body: CreateImageRequest }) =>
      createImage(axiosClient, vars.projectId, vars.body),
    onSuccess(_data, vars) {
      void queryClient.invalidateQueries({ queryKey: ["projectTransactionNotifications", vars.projectId] });
    }
  });
};

export const useCreateVideo = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { readonly projectId: string; readonly body: CreateVideoRequest }) =>
      createVideo(axiosClient, vars.projectId, vars.body),
    onSuccess(_data, vars) {
      void queryClient.invalidateQueries({ queryKey: ["projectTransactionNotifications", vars.projectId] });
    }
  });
};
